<template>
  <div class="visual-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <div class="header-decoration decoration-left"></div>
      <div class="header-title">{{ dashboardTitle }}</div>
      <div class="header-time">{{ currentTime }}</div>
      <div v-if="showReportCenter" class="header-report-menu">
        <v-menu
          bottom
          offset-y
          transition="slide-y-transition"
          content-class="dashboard-report-menu-content"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              small
              depressed
              class="report-menu-btn"
              color="transparent"
              dark
              v-bind="attrs"
              v-on="on"
            >
              报表中心
              <v-icon right small color="#9be7ff">mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense class="dashboard-report-list">
            <v-list-item
              v-for="item in reportMenus"
              :key="item.key"
              @click="goToReport(item)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="header-decoration decoration-right"></div>
      <div class="header-home-group">
        <v-btn icon color="#4fd2dd" class="exit-btn" @click="$router.push('/ins')">
          <v-icon>mdi-home</v-icon>
        </v-btn>
        <img class="header-logo" :src="dashboardLogo" alt="Company logo" />
      </div>
    </div>

    <!-- Main Content -->
    <div class="dashboard-content pa-4">
      <v-row class="dashboard-layout fill-height" dense>
        <!-- Left Column -->
        <v-col cols="12" md="3" class="dashboard-side-column dashboard-left-column">
          <!-- Land Type Stats -->
          <div class="panel-box side-panel">
            <div class="panel-title">土地类型统计</div>
            <div ref="landTypeChart" class="chart-container"></div>
          </div>

          <!-- Cultivated Land -->
          <div class="panel-box side-panel side-panel--compact">
            <div class="panel-title">耕地地类统计</div>
            <div ref="cultivatedChart" class="chart-container"></div>
          </div>

          <!-- Rights Stats Table (Replaces Forest Stats) -->
          <div class="panel-box side-panel" style="overflow: hidden;">
            <div class="panel-title">确权统计</div>
            <div class="table-container pa-2">
              <table class="data-table">
                 <thead>
                   <tr>
                     <th>{{ rightsTableRegionLabel }}</th>
                     <th>确权户数</th>
                     <th>确权面积</th>
                     <th>土地流转</th>
                   </tr>
                 </thead>
                 <tbody>
                    <tr
                      v-for="(item, i) in rightsData"
                      :key="i"
                      :class="{'alt-row': i%2!==0}"
                      style="cursor: pointer;"
                      @click="onSelectTown(item)"
                    >
                      <td>{{ item.name }}</td>
                      <td class="table-number-cell">{{ item.households }}</td>
                      <td class="table-number-cell">{{ item.area }}</td>
                      <td class="table-number-cell">{{ item.transfer }}</td>
                    </tr>
                 </tbody>
              </table>
            </div>
          </div>

          <div class="dashboard-brand-panel" aria-label="合作单位标识">
            <div class="dashboard-brand-logos">
              <div class="brand-logo-group">
                <img src="/static/hangtian_logo.jpg" alt="航天 logo" class="brand-logo brand-logo--hangtian" />
                <span class="brand-logo-text">CASC</span>
              </div>
              <div class="brand-logo-group">
                <img src="/static/dingxin_logo.png" alt="鼎信 logo" class="brand-logo brand-logo--dingxin" />
                <span class="brand-logo-text">DINGXIN</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Center Column -->
        <v-col cols="12" md="6" class="dashboard-center-column">
          <!-- Top Stats Row (Box Style) -->
          <div class="top-stats-row d-flex justify-space-around mb-3">
             <div class="stat-box-large">
                <div class="stat-title">实施行政村数量</div>
                <div class="stat-number">{{ formatStatNumber(topStats.implemented_village_count) }}</div>
             </div>
             <div class="stat-box-large">
                <div class="stat-title">村民小组总数量</div>
                <div class="stat-number">{{ formatStatNumber(topStats.village_group_count) }}</div>
             </div>
             <div class="stat-box-large">
                <div class="stat-title">总户数量</div>
                <div class="stat-number">{{ formatStatNumber(topStats.households_total) }}</div>
             </div>
             <div class="stat-box-large">
                <div class="stat-title">总人口数量</div>
                <div class="stat-number">{{ formatStatNumber(topStats.population_total) }}</div>
             </div>
          </div>

           <!-- Map Area -->
           <div class="map-box relative">
             <div class="map-placeholder">
                <div class="selected-town-tag">当前区域：{{ currentScopeLabel }}</div>
                <v-alert
                  v-if="villageLoadError"
                  dense
                  type="error"
                  border="left"
                  class="map-error-alert"
                >
                  {{ villageLoadError }}
                </v-alert>
                <v-btn
                  v-if="canDrillUp"
                  x-small
                 outlined
                 color="#67e8ff"
                 class="map-back-btn"
                 @click="onDrillUp"
               >
                 返回上一级
               </v-btn>
              <div ref="dashboardMap" class="dashboard-map"></div>
                <div class="map-glow-bg"></div>
              </div>
              <VillagePlotOverlay
                :visible="villagePlotOverlayVisible"
                :loading="villagePlotLoading"
                :village="selectedVillage"
                @close="closeVillagePlotOverlay"
              />
            </div>

          <!-- Main Area Stats (Replaces Rights Chart) -->
          <div class="panel-box main-area-panel">
            <div class="panel-title">区域综合统计</div>
            <div ref="mainAreaChart" class="chart-container"></div>
          </div>
        </v-col>

        <!-- Right Column -->
        <v-col cols="12" md="3" class="dashboard-side-column dashboard-right-column">
           <!-- Livestock (Re-styled as Stacked Bar/Mixed) -->
           <div class="panel-box side-panel">
            <div class="panel-title">养殖统计</div>
            <div ref="livestockChart" class="chart-container"></div>
          </div>

          <!-- Forest/Grass (Moved from Left) -->
          <div class="panel-box side-panel">
            <div class="panel-title">林草统计</div>
            <div ref="forestChart" class="chart-container"></div>
          </div>

          <!-- Vehicles (Re-styled as Table) -->
          <div class="panel-box side-panel" style="overflow: hidden;">
            <div class="panel-title">车辆统计</div>
            <div class="table-container pa-2">
               <div class="vehicle-grid">
                  <div class="vehicle-header">
                  <span>{{ vehicleTableRegionLabel }}</span><span>轿车</span><span>SUV</span><span>面包</span><span>货车</span><span>摩托</span><span>总数</span>
                  </div>
                  <div v-for="(v, idx) in vehicleData" :key="idx" class="vehicle-row">
                     <span>{{v.town}}</span>
                      <span class="hl vehicle-number-cell">{{v.sedan}}</span>
                      <span class="hl vehicle-number-cell">{{v.suv}}</span>
                      <span class="hl vehicle-number-cell">{{v.van}}</span>
                      <span class="hl vehicle-number-cell">{{v.truck}}</span>
                      <span class="hl vehicle-number-cell">{{v.moto}}</span>
                      <span class="hl total vehicle-number-cell">{{v.total}}</span>
                  </div>
               </div>
            </div>
          </div>

          <!-- Machinery (Re-styled as Progress/Bar List) -->
          <div class="panel-box side-panel">
            <div class="panel-title">农机统计</div>
            <div class="machine-panel-body pa-3 d-flex flex-column justify-space-around fill-height">
               <div v-for="m in machineData" :key="m.name" class="machine-item">
                  <div class="d-flex justify-space-between mb-1">
                     <span class="text-caption cyan--text">{{m.name}}</span>
                     <span class="text-caption white--text">{{m.value}}</span>
                  </div>
                  <v-progress-linear :value="m.percent" color="#4fd2dd" height="6" rounded></v-progress-linear>
               </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapState } from 'vuex'
import { getDashboardVillagesByTown, getVisualDashboard } from '@/api/dashboard'
import { REPORT_PLATFORM_ITEMS } from '@/const/reportPlatform'
import VillagePlotOverlay from '@/components/dashboard/VillagePlotOverlay.vue'
import { LAND_SUB_CATEGORY_LABELS } from '@/const/const.js'

export default {
  name: 'VisualDashboard',
  components: {
    VillagePlotOverlay
  },
  computed: {
    ...mapState(['selectedArea', 'authorizedArea', 'user']),
    isAhicOrg () {
      return String(this.user?.organize || '').trim().toLowerCase() === 'ahic'
    },
    showReportCenter () {
      return !this.isAhicOrg
    },
    dashboardTitle () {
      const org = (this.user?.organize || '').toLowerCase()
      const branding = {
        ahic: '安华农险瓦房店市农业数据可视化平台'
      }
      return branding[org] || '人保财险赤峰市分公司农业数据可视化平台'
    },
    dashboardLogo () {
      const org = (this.user?.organize || '').toLowerCase()
      const logos = {
        ahic: '/static/ahic-logo.png'
      }
      return logos[org] || '/static/en_picc_logo.png'
    },
    currentRegionCode () {
      return this.selectedArea?.place_id || this.selectedArea?.town_code || this.selectedArea?.code || ''
    },
    currentRegionName () {
      return this.selectedArea?.display_name || this.selectedArea?.name || this.selectedArea?.address?.name || ''
    },
    currentMapLevel () {
      if (!this.drilldownStack.length) return 'county'
      return this.drilldownStack[this.drilldownStack.length - 1]?.level || 'county'
    },
    canDrillUp () {
      return this.currentMapLevel !== 'county'
    },
    currentScopeLabel () {
      if (!this.drilldownStack.length) return '旗县（全部授权区域）'
      return this.drilldownStack.map(item => this.resolveDisplayName(item.name, item.code)).join(' / ')
    },
    rightsTableRegionLabel () {
      return this.currentMapLevel === 'county' ? '乡镇名称' : '村名称'
    },
    vehicleTableRegionLabel () {
      return this.currentMapLevel === 'county' ? '乡镇' : '村'
    }
  },
  watch: {
    currentRegionCode: {
      immediate: false,
      async handler () {
        this.resetDrilldown()
        await this.fetchDashboardData()
        this.initCharts()
        this.loadDashboardRegionBoundary()
      }
    }
  },
  data () {
    return {
      currentTime: '',
      timer: null,
      selectedTownName: '',
      selectedTownCode: '',
      drilldownStack: [],
      mapChart: null,
      mapChartName: 'dashboard-selected-region-map',
      charts: {
        landType: null,
        cultivated: null,
        mainArea: null,
        livestock: null,
        forest: null
      },
      topStats: {
        implemented_village_count: 0,
        village_group_count: 0,
        households_total: 0,
        population_total: 0
      },
      rightsData: [],
      vehicleData: [],
      machineData: [],
      villageItemsByTown: {},
      villageLoadError: '',
      landTypeStats: [],
      cultivatedStats: [],
      mainAreaStats: [],
      livestockData: [],
      forestData: [],
      villagePlotOverlayVisible: false,
      villagePlotLoading: false,
      selectedVillage: { code: '', name: '' },
      reportMenus: REPORT_PLATFORM_ITEMS
    }
  },
  async mounted () {
    this.startClock();
    await this.$store.dispatch('fetchAuthRegions')
    await this.fetchDashboardData();
    this.initializeDashboardMapChart();
    this.initCharts();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.charts).forEach(c => c && c.dispose());
    if (this.mapChart) {
      this.mapChart.dispose()
      this.mapChart = null
    }
  },
  methods: {
    async fetchDashboardData () {
      try {
        const params = { year: new Date().getFullYear() }
        if (this.selectedTownCode) {
          params.region_code = this.selectedTownCode
        }
        const { data } = await getVisualDashboard(params)
        this.topStats = data.top_stats || this.topStats
        this.rightsData = data.rights_stats || []
        this.vehicleData = data.vehicle_stats || []
        this.machineData = data.machine_stats || []
        this.landTypeStats = data.land_type_stats || []
        this.cultivatedStats = data.cultivated_stats || []
        this.mainAreaStats = data.main_area_stats || []
        this.livestockData = data.livestock_stats || []
        this.forestData = data.forest_stats || []
        if (this.selectedTownCode) {
          this.selectedTownName = data.selected_region_name || this.selectedTownName || '全部'
        } else {
          this.selectedTownName = '全部'
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      }
    },
    async onSelectTown (item) {
      const code = item?.code || ''
      if (!code) return
      if (this.currentMapLevel === 'town') {
        await this.openVillagePlotOverlay(code, item?.name || code)
        return
      }
      await this.applyDrilldown('town', code, item?.name || code)
    },
    formatStatNumber (value) {
      return Number(value || 0).toLocaleString('zh-CN')
    },
    formatAxisNumber (value) {
      const numeric = Number(value || 0)
      if (Math.abs(numeric) >= 100000000) {
        return `${(numeric / 100000000).toFixed(1)}亿`
      }
      if (Math.abs(numeric) >= 10000) {
        return `${(numeric / 10000).toFixed(1)}万`
      }
      return numeric.toLocaleString('zh-CN')
    },
    formatCompactNumber (value, digits = 1) {
      const numeric = Number(value || 0)
      if (Math.abs(numeric) >= 100000000) {
        return `${(numeric / 100000000).toFixed(digits)}亿`
      }
      if (Math.abs(numeric) >= 10000) {
        return `${(numeric / 10000).toFixed(digits)}万`
      }
      if (Math.abs(numeric) >= 1000) {
        return numeric.toLocaleString('zh-CN')
      }
      return `${Number(numeric.toFixed(digits))}`
    },
    normalizeLandSubCategory (value) {
      return String(value || '').trim().toUpperCase()
    },
    formatLandSubCategoryName (value) {
      const normalized = this.normalizeLandSubCategory(value)
      return LAND_SUB_CATEGORY_LABELS[normalized] || normalized || '未分类'
    },
    getNiceAxisMax (value, splitCount = 4) {
      const numeric = Number(value || 0)
      if (!numeric || numeric <= 0) return splitCount
      const roughStep = numeric / splitCount
      const magnitude = 10 ** Math.floor(Math.log10(roughStep))
      const normalized = roughStep / magnitude
      let niceNormalized = 1
      if (normalized > 5) {
        niceNormalized = 10
      } else if (normalized > 2) {
        niceNormalized = 5
      } else if (normalized > 1) {
        niceNormalized = 2
      }
      const step = niceNormalized * magnitude
      return Math.ceil(numeric / step) * step
    },
    startClock () {
      const update = () => {
        const now = new Date()
        const time = now.toLocaleTimeString('zh-CN', { hour12: false })
        const weekday = now.toLocaleDateString('zh-CN', { weekday: 'long' })
        const date = now.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })
        this.currentTime = `${time} ${weekday} ${date}`
      };
      update();
      this.timer = setInterval(update, 1000);
    },
    goToReport (item) {
      this.$router.push({
        name: 'ReportPlatform',
        params: { reportKey: item.key }
      })
    },

    initCharts () {
      this.$nextTick(() => {
        this.renderLandType();
        this.renderCultivated();
        this.renderMainArea();
        this.renderLivestock();
        this.renderForest();
      });
    },
    initializeDashboardMapChart () {
      if (!this.$refs.dashboardMap) return
      this.mapChart = echarts.init(this.$refs.dashboardMap)
      this.mapChart.on('click', async (params) => {
        const regionCode = params?.data?.regionCode || params?.data?.region_code || params?.data?.code || ''
        const regionName = params?.name || ''
        await this.onMapRegionClick(regionCode, regionName)
      })
      this.loadDashboardRegionBoundary()
    },
    resetDrilldown () {
      this.drilldownStack = []
      this.selectedTownCode = ''
      this.selectedTownName = '全部'
      this.villagePlotOverlayVisible = false
      this.villageLoadError = ''
    },
    async applyDrilldown (level, code, name) {
      if (!code) return
      if (level === 'town') {
        this.drilldownStack = [{ level: 'town', code, name: this.resolveDisplayName(name, code) }]
      } else if (level === 'village') {
        const townCode = `${String(code).slice(0, 9)}000`
        const townFromStack = this.drilldownStack.find(item => item.level === 'town')
        const townName = townFromStack?.name || this.rightsData.find(item => item?.code === townCode)?.name || ''
        this.drilldownStack = [
          { level: 'town', code: townCode, name: this.resolveDisplayName(townName, townCode) },
          { level: 'village', code, name: this.resolveDisplayName(name, code) }
        ]
      }
      this.selectedTownCode = code
      this.selectedTownName = this.resolveDisplayName(name, code)
      await this.fetchDashboardData()
      this.initCharts()
      await this.loadDashboardRegionBoundary()
    },
    async onDrillUp () {
      if (this.currentMapLevel === 'village') {
        const town = this.drilldownStack.find(item => item.level === 'town')
        if (town?.code) {
          this.drilldownStack = [town]
          this.selectedTownCode = town.code
          this.selectedTownName = this.resolveDisplayName(town.name, town.code)
        } else {
          this.resetDrilldown()
        }
      } else {
        this.resetDrilldown()
      }
      await this.fetchDashboardData()
      this.initCharts()
      await this.loadDashboardRegionBoundary()
    },
    async onMapRegionClick (regionCode, regionName) {
      if (!regionCode && regionName) {
        const matchedTown = this.rightsData.find(item => item?.name === regionName && item?.code)
        if (matchedTown) {
          await this.applyDrilldown('town', matchedTown.code, matchedTown.name)
          return
        }
      }
      if (!regionCode) {
        if (regionName) this.selectedTownName = regionName
        return
      }

      if (this.currentMapLevel === 'county') {
        await this.applyDrilldown('town', regionCode, regionName || regionCode)
        return
      }
      if (this.currentMapLevel === 'town') {
        await this.openVillagePlotOverlay(regionCode, regionName || regionCode)
        return
      }
      this.selectedTownName = this.resolveDisplayName(regionName, this.selectedTownCode)
    },
    async openVillagePlotOverlay (villageCode, villageName) {
      if (!villageCode) return
      this.selectedVillage = {
        code: villageCode,
        name: this.resolveDisplayName(villageName, villageCode)
      }
      this.villagePlotLoading = false
      this.villagePlotOverlayVisible = true
    },
    closeVillagePlotOverlay () {
      this.villagePlotOverlayVisible = false
    },
    resolveDisplayName (name, code) {
      const normalized = String(name || '').trim()
      if (normalized && !/^\d{6,}$/.test(normalized)) return normalized
      return '未命名区域'
    },
    getRegionName (item) {
      if (!item || typeof item !== 'object') return ''
      return (
        item.display_name ||
        item.name ||
        item.region_name ||
        item.town_name ||
        item.village_name ||
        item.dist_name ||
        item.NAME ||
        item.Name ||
        item?.address?.name ||
        ''
      )
    },
    getRegionCode (item) {
      if (!item || typeof item !== 'object') return ''
      return item.place_id || item.code || item.town_code || item.village_code || item?.address?.code || item?.address?.place_id || ''
    },
    getFeatureRegionCode (feature) {
      const props = feature?.properties || {}
      const featureId = String(feature?.id || '')
      const featureIdCode = featureId.includes('.') ? featureId.split('.').pop() : featureId
      return (
        props.requestedRegionCode ||
        featureIdCode ||
        props.regionCode ||
        props.region_code ||
        props.regionCode12 ||
        props.region_code_12 ||
        props.place_id ||
        props.code ||
        props.region_id ||
        props.regionId ||
        props.village_code ||
        props.town_code ||
        props.dist_code ||
        props.DIST_CODE ||
        props.adcode ||
        props.AD_CODE ||
        ''
      )
    },
    getFeatureRegionName (feature) {
      const props = feature?.properties || {}
      return (
        props.name ||
        props.region_name ||
        props.regionName ||
        props.town_name ||
        props.village_name ||
        props.dist_name ||
        props.DIST_NAME ||
        props.NAME_CHN ||
        props.NAME ||
        props.Name ||
        ''
      )
    },
    getAuthorizedRegionCodes () {
      const codes = []
      if (Array.isArray(this.authorizedArea) && this.authorizedArea.length > 0) {
        this.authorizedArea.forEach(item => {
          const code = this.getRegionCode(item)
          if (code) codes.push(code)
        })
      }

      if (codes.length === 0) {
        const selectedCode = this.getRegionCode(this.selectedArea)
        if (selectedCode) codes.push(selectedCode)
      }

      if (codes.length === 0 && this.user?.bound_region_code) {
        codes.push(this.user.bound_region_code)
      }

      return [...new Set(codes)]
    },
    getTownItemsForMap () {
      if (Array.isArray(this.rightsData) && this.rightsData.length > 0) {
        return this.rightsData
          .filter(item => item?.code)
          .map(item => ({ code: item.code, name: this.resolveDisplayName(item.name, item.code) }))
      }
      return this.getAuthorizedRegionCodes()
        .map(code => ({ code: `${String(code).slice(0, 9)}000`, name: '未命名区域' }))
        .filter(item => item.code)
    },
    async ensureVillageItemsForTown (townCode) {
      const normalizedTownCode = String(townCode || '').trim()
      if (!normalizedTownCode) return
      if (Array.isArray(this.villageItemsByTown[normalizedTownCode])) return
      try {
        const { data } = await getDashboardVillagesByTown(normalizedTownCode)
        const villages = Array.isArray(data)
          ? data
            .filter(item => item?.code)
            .map(item => ({
              code: String(item.code),
              name: this.resolveDisplayName(item.name, item.code)
            }))
          : []
        this.villageLoadError = ''
        this.$set(this.villageItemsByTown, normalizedTownCode, villages)
      } catch (error) {
        console.error('Failed to load villages for town:', normalizedTownCode, error)
        this.villageLoadError = `村级数据加载失败（区域 ${normalizedTownCode}）：${error?.response?.status || 'NETWORK'}`
        this.$set(this.villageItemsByTown, normalizedTownCode, [])
        throw error
      }
    },
    getVillageItemsForTown (townCode) {
      const normalizedTownCode = String(townCode || '').trim()
      if (!normalizedTownCode) return []
      return this.villageItemsByTown[normalizedTownCode] || []
    },
    async getMapItemsByLevel () {
      if (this.currentMapLevel === 'county') {
        return this.getTownItemsForMap()
      }
      if (this.currentMapLevel === 'town') {
        const townCode = this.drilldownStack.find(item => item.level === 'town')?.code || this.selectedTownCode
        await this.ensureVillageItemsForTown(townCode)
        const villageItems = this.getVillageItemsForTown(townCode)
        if (villageItems.length > 0) return villageItems
        return []
      }
      return this.selectedTownCode ? [{ code: this.selectedTownCode, name: this.resolveDisplayName(this.selectedTownName, this.selectedTownCode) }] : []
    },
    async loadDashboardRegionBoundary () {
      if (!this.mapChart) return
      if (!this.selectedTownCode) this.selectedTownName = '全部'
      const mapItems = await this.getMapItemsByLevel()
      const regionCodes = mapItems.map(item => item.code).filter(Boolean)
      const mapNameByCode = new Map(
        mapItems
          .filter(item => item?.code)
          .map(item => [String(item.code), item.name || item.code])
      )

      if (regionCodes.length === 0) {
        this.mapChart.clear()
        return
      }

      try {
        const geojsonCollection = await Promise.all(
          regionCodes.map(async code => ({
            requestedCode: String(code),
            data: await this.fetchRegionBoundaryGeojson(code)
          }))
        )
        const mergedFeatures = geojsonCollection
          .flatMap(item => (
            Array.isArray(item?.data?.features)
              ? item.data.features.map(feature => ({
                ...feature,
                properties: {
                  ...(feature?.properties || {}),
                  requestedRegionCode: item.requestedCode
                }
              }))
              : []
          ))
        const hasFeatures = mergedFeatures.length > 0
        if (!hasFeatures) {
          this.mapChart.clear()
          return
        }
        const normalizedFeatures = mergedFeatures.map((feature) => {
          const properties = feature?.properties || {}
          const regionCode = String(this.getFeatureRegionCode(feature) || '')
          const regionName = this.resolveDisplayName(
            mapNameByCode.get(regionCode) || this.getFeatureRegionName(feature),
            regionCode
          )
          return {
            ...feature,
            properties: {
              ...properties,
              regionCode,
              region_code: regionCode,
              name: regionName
            }
          }
        })
        const mapRegionData = normalizedFeatures
          .map(feature => ({
            name: feature?.properties?.name || '',
            regionCode: feature?.properties?.regionCode || ''
          }))
          .filter(item => item.name)
        const geojsonData = {
          type: 'FeatureCollection',
          features: normalizedFeatures
        }
        echarts.registerMap(this.mapChartName, geojsonData)
        this.mapChart.setOption({
          tooltip: {
            trigger: 'item',
            formatter: '{b}'
          },
          series: [{
            type: 'map',
            map: this.mapChartName,
            roam: false,
            selectedMode: false,
            data: mapRegionData,
            label: {
              show: true,
              color: '#c8f5ff',
              fontSize: 11
            },
            itemStyle: {
              areaColor: 'rgba(0, 0, 0, 0)',
              borderColor: '#4fd2dd',
              borderWidth: 2
            },
            emphasis: {
              label: { show: false },
              itemStyle: {
                areaColor: 'rgba(79, 210, 221, 0.18)',
                borderColor: '#8af2ff'
              }
            }
          }]
        }, true)
      } catch (error) {
        console.error('Failed to render selected region boundary:', error)
      }
    },
    async fetchRegionBoundaryGeojson (regionCode) {
      const wfsUrl = 'https://geo.kungsong.com/geoserver/ks/wfs'
      const params = new URLSearchParams({
        service: 'WFS',
        version: '1.1.0',
        request: 'GetFeature',
        typeName: 'ks:geo_district_code',
        outputFormat: 'application/json',
        featureId: `geo_district_code.${regionCode}`
      })

      const wfsResponse = await fetch(`${wfsUrl}?${params.toString()}`)
      if (wfsResponse.ok) {
        const wfsData = await wfsResponse.json()
        if (Array.isArray(wfsData?.features) && wfsData.features.length > 0) return wfsData
      }

      return this.$store.dispatch('fetchRegionGeometry', regionCode)
    },

    renderLandType () {
      const chart = echarts.init(this.$refs.landTypeChart);
      this.charts.landType = chart;
      const option = {
        color: ['#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4'],
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [{
          name: '土地类型',
          type: 'pie',
          radius: ['50%', '75%'],
          center: ['50%', '50%'],
          data: this.landTypeStats.map(item => ({ value: Number(item.value || 0), name: item.name })),
          label: { color: '#fff', formatter: '{b}' },
          labelLine: { lineStyle: { color: '#fff' } }
        }]
      };
      chart.setOption(option);
    },

    renderCultivated () {
      const chart = echarts.init(this.$refs.cultivatedChart);
      this.charts.cultivated = chart;
      const cultivatedValues = this.cultivatedStats.map(item => Number(item.value || 0))
      const cultivatedAxisMax = this.getNiceAxisMax(Math.max(...cultivatedValues, 0), 4)
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { top: '8%', bottom: '14%', left: '18%', right: '8%' },
        xAxis: {
          type: 'value',
          max: cultivatedAxisMax,
          interval: cultivatedAxisMax / 4,
          splitLine: { show: false },
          axisLabel: {
            color: '#fff',
            formatter: (value) => this.formatAxisNumber(value)
          },
          axisLine: { show: true, lineStyle: { color: '#555' } }
        },
        yAxis: {
          type: 'category',
          data: this.cultivatedStats.map(item => item.name),
          axisLabel: { color: '#fff' }
        },
        series: [{
          type: 'bar',
          data: this.cultivatedStats.map(item => Number(item.value || 0)),
          barWidth: 20,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [{ offset: 0, color: '#4fd2dd' }, { offset: 1, color: '#2b5876' }])
          },
          label: {
            show: true,
            position: 'right',
            color: '#fff',
            formatter: ({ value }) => this.formatCompactNumber(value)
          }
        }]
      };
      chart.setOption(option);
    },

    renderMainArea () {
      // Center Bottom Chart
      const chart = echarts.init(this.$refs.mainAreaChart);
      this.charts.mainArea = chart;
      const mainAreaCategoryLabels = this.mainAreaStats.map(item => this.formatLandSubCategoryName(item.name))
      const mainAreaValues = this.mainAreaStats.map(item => Number(item.value || 0))
      const mainAreaAxisMax = this.getNiceAxisMax(Math.max(...mainAreaValues, 0), 4)
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { top: '15%', bottom: '18%', left: '10%', right: '5%' },
        legend: { top: 0, textStyle: { color: '#fff' } },
        xAxis: {
          type: 'category',
          data: mainAreaCategoryLabels,
          axisLabel: { color: '#fff', interval: 0 }
        },
        yAxis: {
          type: 'value',
          max: mainAreaAxisMax,
          interval: mainAreaAxisMax / 4,
          splitLine: { lineStyle: { type: 'dashed', color: '#333' } },
          axisLabel: {
            color: '#fff',
            formatter: (value) => this.formatAxisNumber(value)
          }
        },
        series: [{
          name: '面积(亩)',
          data: this.mainAreaStats.map(item => Number(item.value || 0)),
          type: 'bar',
          barWidth: 25,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: '#00f2fe' }, { offset: 1, color: '#4facfe' }])
          },
          label: {
            show: true,
            position: 'top',
            color: '#fff',
            formatter: ({ value }) => this.formatCompactNumber(value)
          }
        }]
      };
      chart.setOption(option);
    },

    renderLivestock () {
      const chart = echarts.init(this.$refs.livestockChart);
      this.charts.livestock = chart;
      const livestockTotals = this.livestockData.map(item => (
        Number(item.poultry || 0) +
        Number(item.sheep || 0) +
        Number(item.pig || 0) +
        Number(item.cattle || 0) +
        Number(item.horse || 0)
      ))
      const livestockAxisMax = this.getNiceAxisMax(Math.max(...livestockTotals, 0), 4)
      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
          data: ['羊', '猪', '牛', '马', '家禽'],
          textStyle: { color: '#fff' },
          right: 10,
          top: 0,
          itemWidth: 10,
          itemHeight: 10
        },
        grid: { top: '20%', bottom: '10%', left: '12%', right: '5%' },
        xAxis: {
          type: 'category',
          data: this.livestockData.map(item => item.region),
          axisLabel: { color: '#fff' },
          axisLine: { lineStyle: { color: '#333' } }
        },
        yAxis: {
          type: 'value',
          max: livestockAxisMax,
          interval: livestockAxisMax / 4,
          splitLine: { lineStyle: { color: '#333', type: 'dashed' } },
          axisLabel: {
            color: '#fff',
            formatter: (value) => this.formatAxisNumber(value)
          }
        },
        series: [
          {
            name: '家禽',
            type: 'bar',
            stack: 'total',
            barWidth: 20,
            data: this.livestockData.map(item => Number(item.poultry || 0)),
            itemStyle: { color: '#FFB74D' } // Orange
          },
          {
            name: '羊',
            type: 'bar',
            stack: 'total',
            data: this.livestockData.map(item => Number(item.sheep || 0)),
            itemStyle: { color: '#e57373' } // Red/Pink
          },
          {
            name: '猪',
            type: 'bar',
            stack: 'total',
            data: this.livestockData.map(item => Number(item.pig || 0)),
            itemStyle: { color: '#64B5F6' } // Blue
          },
          {
            name: '牛',
            type: 'bar',
            stack: 'total',
            data: this.livestockData.map(item => Number(item.cattle || 0)),
            itemStyle: { color: '#81C784' } // Green
          },
          {
            name: '马',
            type: 'bar',
            stack: 'total',
            data: this.livestockData.map(item => Number(item.horse || 0)),
            itemStyle: { color: '#BA68C8' } // Purple
          }
        ]
      };
      chart.setOption(option);
    },

    renderForest () {
      const chart = echarts.init(this.$refs.forestChart);
      this.charts.forest = chart;
      const forestValues = this.forestData.flatMap(item => [
        Number(item.forest || 0),
        Number(item.grass || 0)
      ])
      const forestAxisMax = this.getNiceAxisMax(Math.max(...forestValues, 0), 4)
      const option = {
        tooltip: { trigger: 'axis' },
        legend: { textStyle: { color: '#fff' }, right: 10, top: 0 },
        grid: { top: '20%', bottom: '10%', left: '18%', right: '5%' },
        xAxis: {
          type: 'category',
          data: this.forestData.map(item => item.region),
          axisLabel: { color: '#fff' },
          axisPointer: { type: 'shadow' }
        },
        yAxis: {
          type: 'value',
          max: forestAxisMax,
          interval: forestAxisMax / 4,
          splitLine: { lineStyle: { type: 'dashed', color: '#333' } },
          axisLabel: {
            color: '#fff',
            formatter: (value) => this.formatAxisNumber(value)
          }
        },
        series: [
          { name: '林地', type: 'bar', data: this.forestData.map(item => Number(item.forest || 0)), itemStyle: { color: '#00bfff' } },
          { name: '草地', type: 'bar', data: this.forestData.map(item => Number(item.grass || 0)), itemStyle: { color: '#00ff7f' } }
        ]
      };
      chart.setOption(option);
    },

    handleResize () {
      Object.values(this.charts).forEach(c => c && c.resize());
      if (this.mapChart) this.mapChart.resize()
    }
  }
}
</script>

<style scoped>
.visual-dashboard {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #020308;
  color: #fff;
  background:
    radial-gradient(circle at 50% 70%, rgba(25, 66, 196, 0.45) 0%, rgba(7, 19, 58, 0) 45%),
    radial-gradient(circle at 20% 35%, rgba(66, 33, 157, 0.35) 0%, rgba(66, 33, 157, 0) 40%),
    radial-gradient(circle at center, #13224e 0%, #070d2a 50%, #040818 100%);
  overflow: hidden;
  font-family: 'Microsoft YaHei', sans-serif;
  z-index: 100;
}

.dashboard-header {
  height: 80px;
  background: linear-gradient(180deg, rgba(17, 38, 106, 0.72) 0%, rgba(9, 20, 58, 0.45) 60%, rgba(9, 20, 58, 0) 100%);
  position: relative;
  text-align: center;
  border-bottom: 1px solid rgba(0, 234, 255, 0.3);
}

.header-title {
  max-width: calc(100% - 420px);
  margin: 0 auto;
  padding: 0 120px;
  font-size: 26px;
  font-weight: bold;
  line-height: 80px;
  color: #e0f7fa;
  text-shadow: 0 0 20px rgba(0, 234, 255, 0.5);
  letter-spacing: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-time {
  position: absolute;
  right: 20px;
  top: 28px;
  font-size: 14px;
  color: #67e8ff;
  font-weight: 600;
}

.header-report-menu {
  position: absolute;
  right: 20px;
  top: 52px;
}

.report-menu-btn {
  min-width: 112px;
  height: 26px !important;
  padding: 0 10px !important;
  border: 1px solid rgba(79, 210, 221, 0.32);
  color: #9be7ff !important;
  background: rgba(8, 30, 77, 0.78) !important;
  box-shadow: 0 0 18px rgba(79, 210, 221, 0.18);
}

.dashboard-report-list {
  background: linear-gradient(180deg, rgba(9, 22, 58, 0.96) 0%, rgba(8, 18, 48, 0.96) 100%);
  border: 1px solid rgba(79, 210, 221, 0.28);
  color: #dfffff;
  min-width: 300px;
}

.dashboard-report-list .v-list-item {
  min-height: 38px;
}

.dashboard-report-list .v-list-item__title {
  color: #dfffff;
  font-size: 13px;
}

.header-decoration {
  position: absolute;
  top: 18px;
  width: 84px;
  height: 10px;
  background: radial-gradient(circle, rgba(76, 208, 255, 0.95) 0%, rgba(76, 208, 255, 0) 70%);
  opacity: 0.85;
}

.decoration-left {
  left: calc(50% - 320px);
}

.decoration-right {
  right: calc(50% - 320px);
}

.header-home-group {
  position: absolute;
  left: 20px;
  top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.exit-btn {
  position: static;
}

.header-logo {
  height: 34px;
  width: auto;
  opacity: 0.72;
  object-fit: contain;
  filter: drop-shadow(0 0 4px rgba(103, 232, 255, 0.12));
}

.dashboard-content {
  height: calc(100vh - 80px);
  box-sizing: border-box;
  overflow: hidden;
  padding: 12px 14px 10px !important;
}

.dashboard-content > .row {
  height: 100%;
  margin: 0 !important;
}

.dashboard-layout {
  height: 100%;
}

.dashboard-content .d-flex.flex-column,
.dashboard-content .col-12,
.dashboard-content [class*="col-"] {
  min-height: 0;
}

.dashboard-side-column,
.dashboard-center-column {
  height: 100%;
  min-height: 0;
}

.dashboard-left-column {
  display: grid;
  grid-template-rows: minmax(0, 1fr) minmax(0, 0.68fr) minmax(0, 1.22fr) auto;
  gap: 10px;
}

.dashboard-right-column {
  display: grid;
  grid-template-rows: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.dashboard-center-column {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) minmax(180px, 24%);
  gap: 10px;
}

.side-panel {
  min-height: 0;
}

.side-panel--compact .chart-container {
  min-height: 120px;
}

.dashboard-brand-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border: 1px solid rgba(79, 210, 221, 0.18);
  border-radius: 2px;
  background: rgba(8, 20, 52, 0.36);
  box-shadow: inset 0 0 14px rgba(30, 122, 255, 0.08);
}

.dashboard-brand-logos {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  width: 100%;
  overflow: hidden;
}

.brand-logo-group {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
}

.brand-logo {
  display: block;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.brand-logo--hangtian {
  height: 22px;
}

.brand-logo--dingxin {
  height: 24px;
  filter: drop-shadow(0 0 3px rgba(103, 232, 255, 0.14));
}

.brand-logo-text {
  font-size: 12px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: rgba(214, 247, 255, 0.88);
  white-space: nowrap;
}

/* Panel Styling */
.panel-box {
  background: rgba(14, 24, 72, 0.42);
  border: 1px solid rgba(40, 115, 214, 0.7);
  box-shadow: inset 0 0 28px rgba(30, 122, 255, 0.16), 0 0 10px rgba(0, 100, 255, 0.08);
  position: relative;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
}

.panel-title {
  height: 34px;
  line-height: 34px;
  padding-left: 15px;
  font-size: 16px;
  color: #47d9ff;
  border-left: 3px solid #31d4ff;
  background: linear-gradient(90deg, rgba(47, 164, 255, 0.28) 0%, rgba(10, 20, 55, 0.05) 85%);
  letter-spacing: 1px;
}

.chart-container {
    width: 100%;
    height: 100%;
    flex: 1;
    min-height: 150px;
}

.panel-box::before {
  content: "";
  position: absolute;
  top: -1px;
  left: -1px;
  width: 10px;
  height: 10px;
  border-top: 2px solid #00eaff;
  border-left: 2px solid #00eaff;
}
.panel-box::after {
  content: "";
  position: absolute;
  top: -1px;
  right: -1px;
  width: 10px;
  height: 10px;
  border-top: 2px solid #00eaff;
  border-right: 2px solid #00eaff;
}
.panel-box .panel-corn-bl {
    position: absolute;
    bottom: -1px; left: -1px;
    width: 10px; height: 10px;
    border-bottom: 2px solid #00eaff;
    border-left: 2px solid #00eaff;
}
.panel-box .panel-corn-br {
    position: absolute;
    bottom: -1px; right: -1px;
    width: 10px; height: 10px;
    border-bottom: 2px solid #00eaff;
    border-right: 2px solid #00eaff;
}

/* Big Stats Boxes */
.stat-box-large {
    background: linear-gradient(180deg, rgba(50, 114, 205, 0.3) 0%, rgba(20, 46, 102, 0.4) 100%);
    border: 1px solid rgba(78, 159, 245, 0.85);
    box-shadow: inset 0 0 12px rgba(84, 169, 255, 0.3);
  padding: 6px 10px;
  min-width: 0;
  height: 58px;
    display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
    position: relative;
}
.stat-title {
    color: #d4f6ff;
  font-size: 12px;
  line-height: 1.2;
    margin-bottom: 0;
}
.stat-number {
  width: 100%;
  text-align: right;
  font-size: clamp(24px, 1.8vw, 34px);
  line-height: 1;
    font-weight: bold;
    color: #e9fbff;
    font-family: 'DIN Alternate', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    letter-spacing: 1px;
}

/* Map Area */
.map-box {
  border: 1px solid rgba(38, 113, 218, 0.65);
  min-height: 0;
  box-shadow: inset 0 0 25px rgba(20, 79, 177, 0.35);
}
.map-placeholder {
    width: 100%; height: 100%;
    position: relative;
    display: flex; justify-content: center; align-items: center;
    background:
      radial-gradient(circle at center, rgba(24, 66, 182, 0.45) 0%, rgba(15, 31, 79, 0.62) 42%, rgba(10, 18, 45, 0.95) 100%);
    background-size: cover;
}
.map-glow-bg {
    position: absolute; top:0; left:0; right:0; bottom:0;
    background: radial-gradient(circle, rgba(0,234,255,0.1) 0%, rgba(0,0,0,0.8) 90%);
    pointer-events: none;
}

.dashboard-map {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.selected-town-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
  padding: 4px 10px;
  font-size: 12px;
  color: #d8f7ff;
  background: rgba(8, 26, 76, 0.7);
  border: 1px solid rgba(65, 206, 255, 0.7);
  border-radius: 2px;
}

.map-back-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
}

.map-error-alert {
  position: absolute;
  top: 42px;
  left: 10px;
  right: 10px;
  z-index: 3;
  margin: 0;
}

.top-stats-row {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 0 !important;
}

.main-area-panel {
  height: auto;
  min-height: 0;
}

/* Tables */
.table-container {
    width: 100%; height: 100%;
    overflow-y: auto;
}

.machine-panel-body {
  min-height: 0;
  padding: 10px 12px !important;
}
.data-table {
    width: 100%; border-collapse: collapse; font-size: 12px;
}
.data-table th {
    text-align: center; padding: 5px;
    color: #4fd2dd;
    border-bottom: 1px solid #14356e;
}
.data-table td {
    padding: 5px;
    color: #d1d1d1;
    border-bottom: 1px solid rgba(20, 53, 110, 0.5);
}
.data-table .table-number-cell {
    text-align: right;
}
.alt-row {
    background: rgba(255,255,255,0.03);
}

/* Vehicle Table Grid Specifics */
.vehicle-grid {
    display: flex; flex-direction: column; font-size: 12px;
}
.vehicle-header, .vehicle-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
    padding: 5px 0;
    border-bottom: 1px solid rgba(20, 53, 110, 0.5);
}
.vehicle-header { color: #4fd2dd; font-weight: bold; text-align: center; }
.vehicle-row { color: #ccc; }
.vehicle-row .hl { color: #fff; text-align: center; }
.vehicle-row .vehicle-number-cell { text-align: right; }
.vehicle-row .total { color: #ffeb3b; }

</style>
